import React from 'react';
import { flatten2dBuffers } from './audioFunctions';
import { pitchTransform } from './pitchShifter';

export default function useBufferPlayer(
    audioContext,
    rawBuffer,
    onPlaybackEnd,
    paused
) {
    const [buffer, setBuffer] = React.useState(null);
    const [duration, setDuration] = React.useState(null);
    const [elapsed, setElapsed] = React.useState(null);

    const startedPlayingAt = React.useRef(null);
    const timeoutRef = React.useRef(null);
    const [source, setSource] = React.useState(null);
    const [playing, setPlaying] = React.useState(false);

    // When getting a raw buffer, make it nice and audio ready in an audiobuffer.
    React.useEffect(() => {
        if (!rawBuffer) return;
        const flat = flatten2dBuffers(rawBuffer);
        const audioBuffer = audioContext.createBuffer(
            1,
            flat.length,
            audioContext.sampleRate
        );

        const channelData = audioBuffer.getChannelData(0);

        for (let i = 0; i < flat.length; i++) {
            channelData[i] = flat[i];
        }

        // Shift pitch here
        let pitchShiftPromise = pitchTransform(audioBuffer, -0.5);
        pitchShiftPromise.then(newBuffer => {
            setBuffer(newBuffer);
        });
        // setBuffer(audioBuffer);
    }, [audioContext, rawBuffer]);

    const makeSource = React.useCallback(
        buffer => {
            const src = audioContext.createBufferSource();
            src.connect(audioContext.destination);
            src.buffer = buffer;
            if (src.detune) {
                src.detune.value = -100;
            }
            return src;
        },
        [audioContext]
    );

    // Construct source node and start playing on first time
    React.useEffect(() => {
        if (!buffer) return;

        const src = makeSource(buffer);
        setSource(src);
        setPlaying(false);
        setDuration(buffer.duration);
        setElapsed(0);
    }, [buffer, onPlaybackEnd, audioContext, makeSource]);

    // If we pause construct a new src ready to play.
    React.useEffect(() => {
        if (!buffer) return;
        if (elapsed === 0) return;

        console.log('reconstruct src');
        const src = makeSource(buffer);
        setSource(src);
    }, [buffer, audioContext, elapsed, makeSource]);

    const timeoutCallback = React.useCallback(
        time => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                onPlaybackEnd && onPlaybackEnd();
            }, time * 1000);
        },
        [onPlaybackEnd]
    );

    React.useState(() => {
        if (duration) {
            timeoutCallback(duration);
        }
    }, [duration]);

    // This hook is the one that starts/stops
    React.useEffect(() => {
        if (!source) return;
        if (paused && playing) {
            console.log('PAUSING AUDIO');
            const now = performance.now() / 1000;
            const last = startedPlayingAt.current;
            const timeSince = now - last;
            source.stop();
            setPlaying(false);
            setElapsed(elapsed => elapsed + timeSince);
            clearTimeout(timeoutRef.current);
        } else if (!paused && !playing && rawBuffer) {
            const remaining = duration - elapsed;
            if (remaining > 0) {
                console.log('PLAYING RECORDING', remaining, 'left');
                timeoutCallback(remaining);
                source.start(0, elapsed);
                startedPlayingAt.current = performance.now() / 1000;
                setPlaying(true);
            }
        }
    }, [
        paused,
        duration,
        elapsed,
        timeoutCallback,
        source,
        playing,
        rawBuffer,
    ]);
}
